/**
 * 商品图主色轻量采样（首页 Phase 2 §9-§11/§13/§29 + 大卡 full-bleed 补充规则，2026-09-18）
 *
 * 用途：左侧「猜你需要」大卡——商品图 cover 铺满图区，图区底部叠采样色渐变过渡带（veil），
 *   其下紧接采样色纯色实底条（base；DOM 上实底条包住商品名，高度结构性=名高——
 *   纯色上缘恒等于名字 top，1 行/2 行名都精确对齐，不再用卡高百分比估算）：
 *   veil = band-only 渐变（自顶 0% 全透明 → 自底 100% 实色；容器高度由组件 CSS 定 ~60px 过渡带）；
 *   base = 采样色实色（实底条背景 + 图片载入期图区兜底底色，图未到也不露白边/灰边）。
 *   文字明暗按采样色亮度自适应（深底白字 / 浅底深字）。
 * 实现：12×12 canvas 均值采样（约 144 像素，无重型图像库）；
 *   crossOrigin='anonymous' —— 同源相对路径（dev 代理/生产同源）零成本，
 *   跨域图（myqcloud 等）未配 CORS 头时 onerror / getImageData 抛 SecurityError →
 *   hash 稳定柔和色 fallback（按 URL/product_id 确定性取色，永不黑色）。
 * 缓存（§29）：模块级 Map，会话内按 URL 复用，容量 200 FIFO。
 */

const CACHE_MAX = 200
const cache = new Map() // url → { base, veil, dark }

// veil 为 band-only 渐变（0%→100% 全高 0→1 透明度）：渐变只做图与纯色条的交接过渡，
// 不再携带纯色段——纯色实底改由组件内 .agg-hero-solid 实底条承担（高度=商品名高度，
// 结构性对齐，2026-09-18 第三轮修订）。过渡带自身高度由组件 CSS（.agg-hero-veil height）控制。

// RGB → 过渡带渐变 + 实底条背景 + 文字明暗。dark=true 表示采样色偏深 → 用白字
function fromRgb (r, g, b) {
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  return {
    base: `rgb(${r}, ${g}, ${b})`,
    veil: `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 1) 100%)`,
    dark: lum < 140
  }
}

// hash → 稳定柔和色（跨域/加载失败兜底）。2026-09-19 生产实拍修正：COS 未配 CORS 时
// 跨域采样必败、有图商品全部落到这里，原 hash%360 全随机色相会冒出与商品毫无关系的
// 绿/粉/蓝卡片底——收敛到品牌蓝灰窄段（215~245，贴主色 #3657FF 家族）+ 低饱和，
// 采样失败时呈现"统一品牌中性色"而不是假装取到了主色。永不出黑底。
function hashFallback (key) {
  const s = String(key || 'x')
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0
  const hue = 215 + (h % 30)
  const c = (a) => `hsla(${hue}, 22%, 78%, ${a})`
  return { base: `hsl(${hue}, 22%, 78%)`, veil: `linear-gradient(180deg, ${c(0)} 0%, ${c(1)} 100%)`, dark: false }
}

const put = (key, v) => {
  cache.set(key, v)
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value) // FIFO 淘汰
  return v
}

/**
 * 采样商品图主色。
 * @param {string} url 图片地址（$img 解析后的最终 URL；data URI 占位图同样可采）
 * @param {string|number} fallbackKey 跨域失败时的稳定取色键（product_id）
 * @returns {Promise<{base:string, veil:string, dark:boolean}>}
 */
export function sampleDominant (url, fallbackKey) {
  const key = url || ('pid:' + fallbackKey)
  if (cache.has(key)) return Promise.resolve(cache.get(key))
  if (!url) return Promise.resolve(put(key, hashFallback(fallbackKey)))
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    let settled = false
    const done = (v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(put(key, v)) } }
    const timer = setTimeout(() => done(hashFallback(fallbackKey)), 3000) // 慢图不卡卡片：3s 兜底
    img.onload = () => {
      try {
        const S = 12
        const c = document.createElement('canvas')
        c.width = S; c.height = S
        const ctx = c.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0, S, S)
        const d = ctx.getImageData(0, 0, S, S).data // taint 时抛 SecurityError → catch
        let r = 0; let g = 0; let b = 0; const n = d.length / 4
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2] }
        done(fromRgb(Math.round(r / n), Math.round(g / n), Math.round(b / n)))
      } catch (e) {
        done(hashFallback(fallbackKey))
      }
    }
    img.onerror = () => done(hashFallback(fallbackKey))
    img.src = url
  })
}

/** 采样前的中性默认（浅蓝灰系 + 深字），避免采样落地前的黑底/白底闪现（veil 同 band-only 结构） */
export const NEUTRAL_CARD_COLORS = { base: 'rgb(226, 230, 238)', veil: 'linear-gradient(180deg, rgba(226, 230, 238, 0) 0%, rgba(226, 230, 238, 1) 100%)', dark: false }

/** 白字可读压暗配方（专区页头部等「文字恒白、封面主色衍生渐变」场景，2026-09-18）：
 *  采样色整体乘 factor 压暗 → solid 实底 + veil 全高 0→1 渐变 + band 压字带
 *  （band：上 ~42% 全透保持原图清晰，下段快速加深到实色——文字直压图上场景）。 */
export function toneDown (base, factor = 0.5) {
  const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(String(base || ''))
  if (!m) {
    return {
      solid: 'rgb(38, 44, 58)',
      veil: 'linear-gradient(180deg, rgba(38, 44, 58, 0) 0%, rgba(38, 44, 58, 1) 100%)',
      band: 'linear-gradient(180deg, rgba(38, 44, 58, 0) 0%, rgba(38, 44, 58, 0) 42%, rgba(38, 44, 58, 0.85) 74%, rgba(38, 44, 58, 1) 100%)'
    }
  }
  const r = Math.round(Number(m[1]) * factor)
  const g = Math.round(Number(m[2]) * factor)
  const b = Math.round(Number(m[3]) * factor)
  return {
    solid: `rgb(${r}, ${g}, ${b})`,
    veil: `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 1) 100%)`,
    band: `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 0) 42%, rgba(${r}, ${g}, ${b}, 0.85) 74%, rgba(${r}, ${g}, ${b}, 1) 100%)`
  }
}
